import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Calendar, Briefcase, MapPin, Clock, CheckCircle,
  AlertCircle, Video, FileText, User, ChevronRight
} from 'lucide-react';

interface Application {
  id: string;
  jobId: string;
  jobTitle: string;
  department: string;
  status: 'interview_pending' | 'interviewed' | 'reviewed' | 'accepted' | 'rejected' | 'ai_pending' | 'human_pending';
  appliedAt: string;
  interviewDate?: string;
  interviewLink?: string;
}

interface InterviewSession {
  id: string;
  jobId: string;
  jobTitle: string;
  department?: string;
  status: 'in_progress' | 'completed' | 'abandoned' | 'cancelled';
  started_at: string;
  finished_at?: string;
  interviewUrl: string;
}

interface ApplicationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
  interviewSession?: InterviewSession;
  onStartInterview?: (jobId: string) => void;
  onContinueInterview?: (interviewUrl: string) => void;
  onViewJobs?: () => void;
}

const ApplicationDetailModal: React.FC<ApplicationDetailModalProps> = ({
  isOpen,
  onClose,
  application,
  interviewSession,
  onStartInterview,
  onContinueInterview,
  onViewJobs
}) => {
  if (!isOpen || !application) return null;

  const getStatusPillColor = (status: string) => {
    const colors: { [key: string]: string } = {
      // Active Interview States
      'In Progress': 'bg-primary-500/10 text-primary-400 border border-primary-500/20',
      'Ready to Begin': 'bg-primary-500/10 text-primary-400 border border-primary-500/20',
      'Ready': 'bg-primary-500/10 text-primary-400 border border-primary-500/20',
      'Interview Available': 'bg-primary-500/10 text-primary-400 border border-primary-500/20',

      // Completed Interview States
      'Interview Completed': 'bg-green-500/10 text-green-400 border border-green-500/20',
      'Completed': 'bg-green-500/10 text-green-400 border border-green-500/20',

      // Review States
      'Under Review': 'bg-purple-500/10 text-purple-400 border border-purple-500/20',
      'Review in Progress': 'bg-purple-500/10 text-purple-400 border border-purple-500/20',

      // Success States - with subtle animation only
      'Congratulations!': 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 animate-pulse',
      'Accepted': 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 animate-pulse',

      // Rejection States
      'Application Not Selected': 'bg-red-500/10 text-red-400 border border-red-500/20',
      'Not Selected': 'bg-red-500/10 text-red-400 border border-red-500/20',
      'Interview Incomplete': 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',

      // General Application States
      'Applied': 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20',
      'Application Received': 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20',
      'Waiting': 'bg-gray-500/10 text-gray-400 border border-gray-500/20',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-400 border border-gray-500/20';
  };

  const getDetailedApplicationStatus = () => {
    // Implement Chart D logic: combine job_applications.status + interview_session.status
    switch (application.status) {
      case 'ai_pending':
        if (!interviewSession) {
          return {
            message: 'Ready to Begin',
            description: 'Your AI interview is ready to start. Click below when you\'re prepared.',
            color: 'bg-gradient-to-r from-primary-400/20 to-primary-500/20 text-primary-300',
            actionText: 'Start Interview',
            actionColor: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/25',
            canTakeAction: true,
            actionType: 'start_interview'
          };
        }

        switch (interviewSession.status) {
          case 'in_progress':
            return {
              message: 'In Progress',
              description: 'Continue your interview where you left off. Your progress has been saved.',
              color: 'bg-gradient-to-r from-primary-500/20 to-primary-600/20 text-primary-300',
              actionText: 'Continue Interview',
              actionColor: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/25',
              canTakeAction: true,
              actionType: 'continue_interview'
            };

          case 'completed':
            return {
              message: 'Under Review',
              description: 'Interview completed successfully. Our team is now reviewing your responses.',
              color: 'bg-gradient-to-r from-purple-500/20 to-primary-500/20 text-purple-300',
              actionText: 'Review in Progress',
              actionColor: 'bg-gradient-to-r from-purple-500 to-primary-500 shadow-lg shadow-purple-500/20',
              canTakeAction: false
            };

          default:
            return {
              message: 'Ready to Begin',
              description: 'Your AI interview is ready to start.',
              color: 'bg-gradient-to-r from-primary-400/20 to-primary-500/20 text-primary-300',
              actionText: 'Start Interview',
              actionColor: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/25',
              canTakeAction: true,
              actionType: 'start_interview'
            };
        }

      case 'interview_pending':
      case 'human_pending':
        if (interviewSession?.status === 'completed') {
          return {
            message: 'Under Review',
            description: 'Interview completed successfully. Our team is now reviewing your assessment.',
            color: 'bg-gradient-to-r from-purple-500/20 to-primary-500/20 text-purple-300',
            actionText: 'Review in Progress',
            actionColor: 'bg-gradient-to-r from-purple-500 to-primary-500 shadow-lg shadow-purple-500/20',
            canTakeAction: false
          };
        }
        return {
          message: 'Interview Available',
          description: 'Your interview session is ready. Complete it to proceed with your application.',
          color: 'bg-gradient-to-r from-primary-400/20 to-primary-500/20 text-primary-300',
          actionText: 'Start Interview',
          actionColor: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/25',
          canTakeAction: true,
          actionType: 'start_interview'
        };

      case 'reviewed':
        return {
          message: 'Under Review',
          description: 'Our team is carefully reviewing your interview results. We\'ll be in touch with next steps.',
          color: 'bg-gradient-to-r from-purple-500/20 to-primary-500/20 text-purple-300',
          actionText: 'Review in Progress',
          actionColor: 'bg-gradient-to-r from-purple-500 to-primary-500 shadow-lg shadow-purple-500/20',
          canTakeAction: false
        };

      case 'interviewed':
        return {
          message: 'Interview Process',
          description: 'Additional interviews may be scheduled. We\'ll contact you with any next steps.',
          color: 'bg-gradient-to-r from-indigo-500/20 to-primary-500/20 text-indigo-300',
          actionText: 'Interviews Ongoing',
          actionColor: 'bg-gradient-to-r from-indigo-500 to-primary-500 shadow-lg shadow-indigo-500/20',
          canTakeAction: false
        };

      case 'accepted':
        return {
          message: 'Congratulations!',
          description: 'Your application has been accepted! Please check your email for the next steps and further instructions.',
          color: 'bg-gradient-to-r from-emerald-500/20 via-primary-500/20 to-emerald-500/20 text-emerald-300 animate-pulse',
          actionText: 'Accepted',
          actionColor: 'bg-gradient-to-r from-emerald-500 via-primary-500 to-emerald-500 shadow-xl shadow-emerald-500/30 animate-pulse',
          canTakeAction: false
        };

      case 'rejected':
        const hasCompletedInterview = interviewSession?.status === 'completed';
        if (hasCompletedInterview) {
          return {
            message: 'Not Selected',
            description: 'Thank you for completing our interview process. While we won\'t be moving forward with your application, we encourage you to apply for other positions.',
            color: 'bg-red-500/20 text-red-400',
            actionText: 'View Other Jobs',
            actionColor: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/25',
            canTakeAction: true,
            actionType: 'view_jobs'
          };
        } else {
          return {
            message: 'Application Not Selected',
            description: 'Thank you for your interest in this position. We encourage you to explore other opportunities with us.',
            color: 'bg-red-500/20 text-red-400',
            actionText: 'View Other Jobs',
            actionColor: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/25',
            canTakeAction: true,
            actionType: 'view_jobs'
          };
        }

      default:
        return {
          message: 'Application Received',
          description: 'Your application has been received and is being processed.',
          color: 'bg-gradient-to-r from-gray-500/20 to-primary-500/20 text-gray-300',
          actionText: 'Waiting',
          actionColor: 'bg-gradient-to-r from-gray-600 to-primary-600 shadow-lg shadow-gray-600/20',
          canTakeAction: false
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCurrentStep = () => {
    // Check if AI interview is actively in progress
    if (interviewSession?.status === 'in_progress') {
      return 'ai-interview';
    }

    if (['accepted', 'rejected'].includes(application.status)) {
      return 'decision';
    } else if (['reviewed', 'interviewed'].includes(application.status)) {
      return 'team-review';
    } else if (application.status === 'human_pending' && (!interviewSession || interviewSession.status === 'completed')) {
      return 'team-review';
    } else if (application.status === 'ai_pending' && interviewSession?.status === 'completed') {
      return 'team-review';
    } else if (application.status === 'ai_pending') {
      return 'ai-interview';
    } else {
      return 'ai-interview';
    }
  };

  const currentStep = getCurrentStep();

  const handleAction = (actionType: string) => {
    switch (actionType) {
      case 'start_interview':
        if (onStartInterview) {
          onStartInterview(application.jobId);
        }
        onClose();
        break;
      case 'continue_interview':
        if (onContinueInterview && interviewSession?.interviewUrl) {
          onContinueInterview(interviewSession.interviewUrl);
        }
        onClose();
        break;
      case 'view_jobs':
        if (onViewJobs) {
          onViewJobs();
        }
        onClose();
        break;
      default:
        break;
    }
  };

  const statusInfo = getDetailedApplicationStatus();

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xl flex items-center justify-center p-6 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 40 }}
            transition={{ type: "spring", duration: 0.4, bounce: 0.2 }}
            className="bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white/5 backdrop-blur-2xl border-b border-white/10 px-4 sm:px-8 py-4 sm:py-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-2xl font-semibold text-white mb-2 truncate">{application.jobTitle}</h2>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-300">
                    <span className="flex items-center gap-1.5">
                      <Briefcase className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate">{application.department}</span>
                    </span>
                    <span className="hidden sm:flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      Applied {formatDate(application.appliedAt)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 sm:p-2.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl sm:rounded-2xl transition-all duration-200 hover:scale-105 flex-shrink-0"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Current Status */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6"
              >
                <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 bg-gradient-to-br from-amber-500/20 to-orange-500/20 backdrop-blur-xl rounded-lg sm:rounded-xl border border-amber-500/20">
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  Application Status
                </h3>

                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium backdrop-blur-sm border ${getStatusPillColor(statusInfo.message)}`}>
                      {statusInfo.message}
                    </span>
                  </div>

                  {/* Hide description on mobile for less clutter */}
                  <p className="hidden sm:block text-gray-300 leading-relaxed text-sm">
                    {statusInfo.description}
                  </p>

                  {statusInfo.canTakeAction && (
                    <div className="pt-2">
                      <button
                        onClick={() => handleAction(statusInfo.actionType!)}
                        className={`w-full sm:w-auto px-6 py-3 rounded-xl text-white text-sm sm:text-base font-medium transition-all duration-200 hover:scale-105 shadow-lg ${statusInfo.actionColor}`}
                      >
                        {statusInfo.actionText}
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Interview Details - Compact on mobile */}
              {interviewSession && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6"
                >
                  <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-xl rounded-lg sm:rounded-xl border border-blue-500/20">
                      <Video className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    Interview Session
                  </h3>

                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-between items-center py-1.5 sm:py-2">
                      <span className="text-gray-400 text-sm">Started:</span>
                      <span className="text-white text-sm">{formatDate(interviewSession.started_at)}</span>
                    </div>

                    {interviewSession.finished_at && (
                      <div className="flex justify-between items-center py-1.5 sm:py-2">
                        <span className="text-gray-400 text-sm">Completed:</span>
                        <span className="text-white text-sm">{formatDate(interviewSession.finished_at)}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center py-1.5 sm:py-2">
                      <span className="text-gray-400 text-sm">Status:</span>
                      <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm border ${getStatusPillColor(
                        interviewSession.status === 'in_progress' ? 'In Progress' :
                        interviewSession.status === 'completed' ? 'Completed' : 'Interview Incomplete'
                      )}`}>
                        {interviewSession.status === 'in_progress' ? 'In Progress' :
                         interviewSession.status === 'completed' ? 'Completed' : 'Incomplete'}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Application Process Timeline - Simplified on mobile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-gray-800/40 to-gray-800/20 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 sm:p-6"
              >
                <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                  <div className="p-1.5 sm:p-2 bg-gradient-to-br from-green-500/20 to-teal-500/20 backdrop-blur-xl rounded-lg sm:rounded-xl border border-green-500/20">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  Application Process
                </h3>

                <div className="space-y-2 sm:space-y-4">
                  {/* Step 1: Application Submitted */}
                  <div className="flex items-start gap-2 sm:gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500/20 rounded-full flex items-center justify-center border-2 border-green-500/40">
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                      </div>
                      <div className="w-px h-6 sm:h-8 bg-gray-700 mt-1 sm:mt-2"></div>
                    </div>
                    <div className="flex-1 pb-4 sm:pb-8 min-w-0">
                      <h4 className="font-semibold text-white text-sm sm:text-base">Application Submitted</h4>
                      <p className="text-gray-400 text-xs sm:text-sm truncate sm:whitespace-normal">Submitted on {formatDate(application.appliedAt)}</p>
                    </div>
                  </div>

                  {/* Step 2: AI Interview */}
                  <div className="flex items-start gap-2 sm:gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 ${
                        interviewSession?.status === 'completed' || ['reviewed', 'interviewed', 'accepted', 'rejected'].includes(application.status) ?
                        'bg-green-500/20 border-green-500/40' :
                        currentStep === 'ai-interview' && interviewSession?.status === 'in_progress' ?
                        'bg-primary-500/30 border-primary-500 shadow-lg shadow-primary-500/50 animate-pulse' :
                        currentStep === 'ai-interview' ?
                        'bg-primary-500/20 border-primary-500/40' :
                        'bg-gray-700/40 border-gray-600/40'
                      }`}>
                        {interviewSession?.status === 'completed' || ['reviewed', 'interviewed', 'accepted', 'rejected'].includes(application.status) ? (
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                        ) : currentStep === 'ai-interview' ? (
                          <Video className="w-3 h-3 sm:w-4 sm:h-4 text-primary-400" />
                        ) : (
                          <Video className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                        )}
                      </div>
                      <div className="w-px h-6 sm:h-8 bg-gray-700 mt-1 sm:mt-2"></div>
                    </div>
                    <div className="flex-1 pb-4 sm:pb-8 min-w-0">
                      <h4 className={`font-semibold text-sm sm:text-base ${
                        currentStep === 'ai-interview' && interviewSession?.status === 'in_progress' ?
                        'text-primary-400 animate-pulse' : 'text-white'
                      }`}>AI Interview</h4>
                      <p className={`text-xs sm:text-sm ${
                        currentStep === 'ai-interview' && interviewSession?.status === 'in_progress' ?
                        'text-primary-300 font-medium' : 'text-gray-400'
                      }`}>
                        {interviewSession?.status === 'completed' || ['reviewed', 'interviewed', 'accepted', 'rejected'].includes(application.status) ? 'Completed successfully' :
                         interviewSession?.status === 'in_progress' ? '🔴 In progress - Continue now' :
                         application.status === 'ai_pending' ? 'Ready to start' : 'Completed'}
                      </p>
                    </div>
                  </div>

                  {/* Step 3: Team Review */}
                  <div className="flex items-start gap-2 sm:gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 ${
                        ['accepted', 'rejected'].includes(application.status) ?
                        'bg-green-500/20 border-green-500/40' :
                        currentStep === 'team-review' && interviewSession?.status !== 'in_progress' ?
                        'bg-purple-500/20 border-purple-500/40 animate-pulse' :
                        interviewSession?.status === 'in_progress' ?
                        'bg-gray-700/40 border-gray-600/40' :
                        (application.status === 'human_pending' || (application.status === 'ai_pending' && interviewSession?.status === 'completed')) ?
                        'bg-primary-500/20 border-primary-500/40' :
                        'bg-gray-700/40 border-gray-600/40'
                      }`}>
                        {['accepted', 'rejected'].includes(application.status) ? (
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                        ) : (
                          <User className={`w-3 h-3 sm:w-4 sm:h-4 ${
                            currentStep === 'team-review' && interviewSession?.status !== 'in_progress' ?
                            'text-purple-400' :
                            interviewSession?.status === 'in_progress' ?
                            'text-gray-500' :
                            (application.status === 'human_pending' || (application.status === 'ai_pending' && interviewSession?.status === 'completed')) ?
                            'text-primary-400' : 'text-gray-500'
                          }`} />
                        )}
                      </div>
                      <div className="w-px h-6 sm:h-8 bg-gray-700 mt-1 sm:mt-2"></div>
                    </div>
                    <div className="flex-1 pb-4 sm:pb-8 min-w-0">
                      <h4 className="font-semibold text-white text-sm sm:text-base">Team Review</h4>
                      <p className="text-gray-400 text-xs sm:text-sm">
                        {['accepted', 'rejected'].includes(application.status) ?
                         'Completed - Decision made' :
                         ['reviewed', 'interviewed'].includes(application.status) ?
                         'Under review' :
                         interviewSession?.status === 'in_progress' ?
                         'Pending AI interview completion' :
                         application.status === 'human_pending' ?
                         'Awaiting team review' :
                         (application.status === 'ai_pending' && interviewSession?.status === 'completed') ?
                         'Awaiting team review' :
                         'Pending AI interview completion'}
                      </p>
                    </div>
                  </div>

                  {/* Step 4: Decision */}
                  <div className="flex items-start gap-2 sm:gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 ${
                        currentStep === 'decision' && application.status === 'accepted' ?
                        'bg-emerald-500/20 border-emerald-500/40 animate-pulse' :
                        currentStep === 'decision' && application.status === 'rejected' ?
                        'bg-red-500/20 border-red-500/40 animate-pulse' :
                        'bg-gray-700/40 border-gray-600/40'
                      }`}>
                        {application.status === 'accepted' ? (
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-400" />
                        ) : application.status === 'rejected' ? (
                          <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
                        ) : (
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-white text-sm sm:text-base">Decision</h4>
                      <p className="text-gray-400 text-xs sm:text-sm">
                        {application.status === 'accepted' ? 'Congratulations! You\'ve been accepted' :
                         application.status === 'rejected' ? 'Application not selected' :
                         'Awaiting final decision'}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ApplicationDetailModal;